import React, { useState } from 'react';
import Select, { ActionMeta } from 'react-select';
import AsyncSelect from 'react-select/async';
import {
  fetchBuildingsRequest,
  lookupBuildings,
  searchBuildingOptions,
  toBuildingOption
} from '~/api/building';
import { Colors } from '~/enums/Colors';
import { SelectType } from '~/enums/SelectType';
import { ISelectOption } from '~/helpers/interfaces';
import { SelectedOptionType } from '~/types/form';

// Same props as Select; the async-only ones are spread in below.
const AsyncBuildingSelect: any = AsyncSelect;

type Props = {
  options?: { label: string; value: string }[];
  onChange?: (selectedOption: SelectedOptionType | null) => void;
  placeholder?: string;
  defaultValue?: SelectedOptionType | null;
  isSearchable?: boolean;
  selectType: string;
  disabled?: boolean;
  isClearable?: boolean;
  noOptionsMessage?: string;
  isError?: boolean | null;
  multiValue: { label: string; value: string }[];
  setMultiValue: any;
  cobuPlusBuildings: Array<string>;
  homebodyBuildings?: Array<string>;
  singleSelect?: boolean;
  defaultOptions?: ISelectOption[];
  // Search buildings on the server instead of filtering `options`.
  asyncBuildings?: boolean;
  isOptionDisabled?: (option: any) => boolean;
  filterOption?: (option: any, input: string) => boolean;
};

const MultiSelect = (props: Props) => {
  const {
    options = [],
    placeholder,
    isSearchable,
    selectType,
    disabled,
    isClearable,
    noOptionsMessage,
    isError,
    multiValue,
    setMultiValue,
    cobuPlusBuildings,
    homebodyBuildings = [],
    singleSelect,
    defaultOptions,
    asyncBuildings,
    isOptionDisabled,
    filterOption
  } = props;

  const [selectAll, setSelectAll] = useState(false);
  const [selectAllCobuPlus, setSelectAllCobuPlus] = useState(false);
  const [selectAllHomebody, setSelectAllHomebody] = useState(false);

  const getBorder = (isFocused: boolean) => {
    if (isFocused && selectType === SelectType.Filter) {
      return 'none';
    } else if (selectType != SelectType.Filter) {
      if (isError) {
        return '2px solid #ef4a4a';
      } else {
        return '1px solid rgba(39, 33, 99, 0.1)';
      }
    }
    return 'none';
  };

  const getBackgroundColor = (value: string, isFocused: boolean) => {
    if (isFocused) {
      return 'rgba(39,33,99,0.1)';
    }
    return 'white';
  };

  let headerOptions: ISelectOption[] = [];
  if (!singleSelect && !selectAll) {
    if (selectAllCobuPlus || selectAllHomebody) {
      headerOptions = defaultOptions
        ? defaultOptions
        : [{ value: 'all', label: 'All Buildings' }];
    } else {
      headerOptions = defaultOptions
        ? defaultOptions
        : [
            { value: 'all', label: 'All Buildings' },
            { value: 'allCobuPlus', label: 'All Cobu Plus Buildings' },
            { value: 'allHomebody', label: 'All Homebody Buildings' }
          ];
    }
  }
  const allOptions = [...headerOptions, ...options];

  const loadOptions = (q: string) =>
    searchBuildingOptions(q).then((found) => [...headerOptions, ...found]);

  const lookupNames = async (uuids: string[]) =>
    uuids.length
      ? (await lookupBuildings({ ids: uuids })).map(toBuildingOption)
      : [];

  const findCobuPlusNames = () => {
    const cobuPlusBuildingNames = cobuPlusBuildings.map((cobuPlusBuilding) => {
      const buildingName = options.find((b) => {
        const buildingCode = b.value;
        return buildingCode === cobuPlusBuilding;
      });
      return { value: cobuPlusBuilding, label: buildingName?.label };
    });
    return cobuPlusBuildingNames.filter(
      (building) => building.label !== undefined
    );
  };

  const findHomebodyNames = () => {
    const homebodyBuildingNames = homebodyBuildings.map((homebodyBuilding) => {
      const buildingName = options.find((b) => {
        const buildingCode = b.value;
        return buildingCode === homebodyBuilding;
      });
      return { value: homebodyBuilding, label: buildingName?.label };
    });
    return homebodyBuildingNames.filter(
      (building) => building.label !== undefined
    );
  };

  const changeOnMultiSelect = async (
    newValue: any,
    actionMeta: ActionMeta<any>
  ) => {
    if (singleSelect) {
      setMultiValue([newValue]);
    } else {
      if (
        actionMeta.action === 'remove-value' ||
        actionMeta.action === 'clear'
      ) {
        setSelectAll(false);
        setSelectAllCobuPlus(false);
        setSelectAllHomebody(false);
      }
      if (newValue) {
        const isAllIndex = newValue.findIndex(
          (item: SelectedOptionType) => item.value === 'all'
        );
        const isAllCobuPlusIndex = newValue.findIndex(
          (item: SelectedOptionType) => item.value === 'allCobuPlus'
        );
        const isAllHomebodyIndex = newValue.findIndex(
          (item: SelectedOptionType) => item.value === 'allHomebody'
        );
        if (isAllIndex >= 0) {
          // ponytail: "All Buildings" still pulls the full list, but only when picked
          setMultiValue(
            asyncBuildings
              ? (await fetchBuildingsRequest()).data.map(toBuildingOption)
              : options
          );
          setSelectAll(true);
          return;
        }
        if (isAllCobuPlusIndex >= 0) {
          const cobuPlusBuildingOptions = asyncBuildings
            ? await lookupNames(cobuPlusBuildings)
            : findCobuPlusNames();
          setMultiValue(cobuPlusBuildingOptions);
          setSelectAllCobuPlus(true);
          return;
        }
        if (isAllHomebodyIndex >= 0) {
          const homebodyBuildingOptions = asyncBuildings
            ? await lookupNames(homebodyBuildings)
            : findHomebodyNames();
          setMultiValue(homebodyBuildingOptions);
          setSelectAllHomebody(true);
          return;
        }
        setMultiValue(newValue);
      }
    }
  };

  const SelectComponent: typeof Select = asyncBuildings
    ? AsyncBuildingSelect
    : Select;

  return (
    <div>
      <SelectComponent
        // Remount so the async "All …" header options reload when they change.
        key={`${selectAll}-${selectAllCobuPlus}-${selectAllHomebody}`}
        {...(asyncBuildings
          ? { loadOptions, defaultOptions: true }
          : { options: allOptions })}
        value={multiValue}
        isOptionDisabled={isOptionDisabled}
        filterOption={filterOption}
        isMulti={!singleSelect}
        placeholder={placeholder}
        isSearchable={isSearchable}
        onChange={changeOnMultiSelect}
        closeMenuOnSelect={singleSelect}
        isClearable={isClearable}
        isDisabled={disabled}
        noOptionsMessage={() =>
          noOptionsMessage ? noOptionsMessage : 'No option'
        }
        styles={{
          option: (provided, state) => {
            return {
              fontSize:
                selectType === SelectType.Form ||
                selectType === SelectType.AutoAdjutForm
                  ? '16px'
                  : '12px',
              color: Colors.cobuDarkBlue,
              cursor: 'pointer',
              backgroundColor: getBackgroundColor(
                state.data.value,
                state.isFocused
              ),
              padding: 16,
              fontFamily: 'Roboto',
              fontStyle: 'normal'
            };
          },
          control: (base, state) => {
            return {
              ...base,
              //   minHeight: getHeight(),
              cursor: 'pointer',
              height: 50,
              alignItems: 'baseline',
              width:
                selectType === SelectType.Form ||
                selectType === SelectType.AutoAdjutForm
                  ? 'inherit'
                  : '180px',
              borderRadius: '8px',
              border: getBorder(state.isFocused),
              boxShadow: 'none',
              ...(selectType === SelectType.AutoAdjutForm
                ? { height: undefined }
                : {})
            };
          },
          valueContainer: (provided, state) => ({
            ...provided,
            height: 'inherit',
            alignItems: 'center',
            position: 'initial',
            overflow: selectType !== SelectType.Form ? 'visible' : 'hidden',
            fontFamily: 'Roboto',
            ...(selectType === SelectType.AutoAdjutForm
              ? { overflow: 'auto', maxHeight: window.innerHeight * 0.25 }
              : {})
          }),
          input: (provided, state) => ({
            ...provided
          }),
          indicatorSeparator: (state) => ({
            display: 'none'
          }),
          indicatorsContainer: (provided, state) => ({
            ...provided
            // height: getHeight()
          }),
          singleValue: (provided, state) => ({
            ...provided,
            alignItems: 'center',
            transform:
              selectType === SelectType.Table
                ? 'translateY(-170%)'
                : selectType === SelectType.Form ||
                  selectType === SelectType.AutoAdjutForm
                ? 'translateY(-10%)'
                : 'translateY(-90%)',
            position: 'inherit',
            color: 'rgba(39, 33, 99, 1)'
          })
        }}
      ></SelectComponent>
    </div>
  );
};

export default MultiSelect;
